// TemplateSection.jsx
// -----------------------------------------------------------------------------
// Shows the “+ New” card plus your live templates (no more static placeholders).
//
// • “+ New” card         – opens the form-builder with a blank form.
// • Live templates       – fetched from Firestore by the IDs you list below.
//   - If the template is *published*  ⇒ clicking its image opens `/forms/:id`
//   - If the template is *not*        ⇒ the click is ignored (grey overlay).
//
// Wrapped in React.memo to avoid pointless re-renders when HomePage paginates.
//
// • Tiny in-memory cache that pre-loads the first five screenshots so they
//   repaint instantly the next time the user navigates back in the same tab.
// -----------------------------------------------------------------------------

import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Box,
  ButtonBase,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useStorageUrl } from '../../hooks/useStorageUrl';
import CreateFormWrapperForTemplate from '../../contexts/CreateFormWrapperForTemplate';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useNavigate } from 'react-router-dom';

import {
  collection,
  query,
  where,
  getDocs,
  documentId,
} from 'firebase/firestore';
import { db } from '../../firebase';

// -----------------------------------------------------------------------------
// 1) CONSTANTS
// -----------------------------------------------------------------------------

/**
 * 👉 LIST THE TEMPLATE IDs YOU WANT TO SURFACE (≤ 10 at a time).
 */
const LIVE_TEMPLATE_IDS = [
  'ffb5a3f5-2317-45f9-9f51-84e34891f05b', // Adobe UTM & CID Builder with card
  '6d2a57d4-e476-415f-a07b-fb1234d7e466', // Adobe CID Form w table
  'c74742c1-5030-45f3-8807-fff972b330a8', // Adobe CID Form w card
  'a5d20150-2a03-4cad-8a5f-3eb51f6d9298', //GA4 UTM w table
  'f5c7e1e9-c289-4503-8333-7ebcae0eda36', //GA4 UTM w card
  '6aaf1fc6-b773-4ff4-8b1f-4ff2ebfd4e88', // Google Universal Analytics UTM Form w table
  '7c5e43aa-b7f3-4943-a815-0bc20c826c62', //Google Universal Analytics UTM Form w card
];

// fallback colours if a template has no screenshot
const fallbackColors = ['#4285F4', '#DB4437', '#F4B400', '#0F9D58'];

/** -------------------------------------------------------------------------
 *  Template card dimensions
 *
 *  WHY
 *  - HomeCard uses a 200 × 150 preview area.
 *  - Google-style template strips usually feel like a smaller preview rail,
 *    not a second full-size grid.
 *  - Keep the same 4:3 visual ratio, but scale the template cards to 75%
 *    of the Home card preview size.
 *
 *  NOTE
 *  - These values intentionally mirror HomeCard dimensions.
 *  - If we later centralize Home surface layout constants, these should move
 *    into a shared `components/Home/homeSurfaceLayout.js` file.
 *  ----------------------------------------------------------------------*/
const HOME_CARD_REFERENCE_WIDTH = 200;
const HOME_CARD_REFERENCE_IMAGE_HEIGHT = 150;
const TEMPLATE_CARD_SCALE = 0.75;

const TEMPLATE_CARD_WIDTH = Math.round(
  HOME_CARD_REFERENCE_WIDTH * TEMPLATE_CARD_SCALE
);

const TEMPLATE_IMAGE_HEIGHT = Math.round(
  HOME_CARD_REFERENCE_IMAGE_HEIGHT * TEMPLATE_CARD_SCALE
);

const TEMPLATE_TITLE_HEIGHT = 28;
const TEMPLATE_SECTION_TITLE = 'Start a new form';

// Match the HomePagetest “Recent forms” vertical rhythm.
// HomePagetest uses section-level vertical padding plus a title/control row
// with mb: 2. Keep the template title spacing driven by layout containers,
// not by ad hoc Typography margins.
const TEMPLATE_SECTION_Y_PADDING = 2;
const TEMPLATE_SECTION_TITLE_ROW_MARGIN_BOTTOM = 1;

/** -------------------------------------------------------------------------
 *  Shared Home rail alignment
 *
 *  WHY
 *  - HomePagetest uses a centered rail based on 5 Home cards:
 *    5 × 200px cards + 4 × 20px gaps = 1080px.
 *  - TemplateSection should align to the same left/right rail so the
 *    template strip does not start farther left than the Home grid/table.
 *  - The template cards remain smaller, but the wrapper they live inside
 *    uses the same content rail as the Home forms section.
 *  ----------------------------------------------------------------------*/
const HOME_CARD_GRID_GAP_PX = 20;
const HOME_GRID_MAX_COLUMNS = 5;
const HOME_CONTENT_RAIL_WIDTH =
  (HOME_CARD_REFERENCE_WIDTH * HOME_GRID_MAX_COLUMNS) +
  (HOME_CARD_GRID_GAP_PX * (HOME_GRID_MAX_COLUMNS - 1));
const HOME_CONTENT_X_PADDING = { xs: 2, sm: 3, md: 4 };

const TEMPLATE_CARD_GAP = `${HOME_CARD_GRID_GAP_PX}px`;
const TEMPLATE_GALLERY_PILL_WIDTH = 64;
const TEMPLATE_SCROLL_END_PADDING =
  `${TEMPLATE_GALLERY_PILL_WIDTH + HOME_CARD_GRID_GAP_PX}px`;

/** -------------------------------------------------------------------------
 *  Tiny in-memory cache that survives component unmounts for as long as the tab is open.
 *  We now preload inside TemplateCard (after useStorageUrl resolves a real URL),
 *  so there is no global PRELOAD_COUNT loop here anymore.
 *  -------------------------------------------------------------------------
 */
const imageCache    = new Map();                // Map<url, HTMLImageElement>

/** -------------------------------------------------------------------------
 *  Screenshot Storage URL policy
 *
 *  ENTERPRISE CONTEXT
 *  - Template thumbnails are generated form screenshots.
 *  - The screenshot backend writes them as system screenshot assets.
 *  - useStorageUrl no longer guesses public access from path shape alone,
 *    because private DAM assets can share the same Storage path patterns.
 *  - Therefore screenshot callers must explicitly declare screenshot policy.
 *
 *  This preserves the hardened DAM model:
 *  - screenshots/icons/banners may use public-form-image GCS URLs
 *  - private DAM assets must use signed/backend URLs
 *  ----------------------------------------------------------------------*/
const SCREENSHOT_STORAGE_URL_POLICY = Object.freeze({
  assetType: 'screenshot',
  preferPublicUrl: true,
  allowPublicFallback: true,
});

// -----------------------------------------------------------------------------
// 2) COMPONENT
// -----------------------------------------------------------------------------
const TemplateCard = React.memo(function TemplateCard({
  tpl,
  idx,
  fallbackColors,
  navigate,
}) {
  const isPublished = tpl.status === 'published';

  /* convert Storage paths → HTTPS URLs
   *
   * ENTERPRISE:
   * These are form screenshot assets, not generic DAM assets.
   * Pass the screenshot policy into useStorageUrl so the hook can resolve
   * public-form-image Storage paths directly to environment-aware GCS URLs
   * instead of attempting Firebase Storage getDownloadURL() first.
   */
  const { url: thumbUrl } = useStorageUrl(tpl.thumbPath, {
    ...SCREENSHOT_STORAGE_URL_POLICY,
    debugLabel: 'TemplateSection.thumbnail',
  });

  // NOTE:
  // Always resolve origPath when it exists so we can fall back on <img onError>.
  const { url: origUrl  } = useStorageUrl(tpl.origPath, {
    ...SCREENSHOT_STORAGE_URL_POLICY,
    debugLabel: 'TemplateSection.original',
  });

  /* step-2.5: legacy fallback — some docs store screenshotUrl as a Storage PATH ("assets/...") */
  const legacyPathMaybe =
    (typeof tpl?.legacyUrl === 'string' && tpl.legacyUrl.startsWith('assets/'))
      ? tpl.legacyUrl
      : null;

  const { url: legacyUrlFromPath } = useStorageUrl(legacyPathMaybe, {
    ...SCREENSHOT_STORAGE_URL_POLICY,
    debugLabel: 'TemplateSection.legacyPath',
  });

  const legacyHttpsUrl =
    (typeof tpl?.legacyUrl === 'string' && /^https?:\/\//i.test(tpl.legacyUrl))
      ? tpl.legacyUrl
      : null;

  /* final pick order */
  const imageUrlRaw =
        thumbUrl
     || origUrl
     || legacyUrlFromPath
     || legacyHttpsUrl
     || null;

  /* Normalize: never allow a raw Storage path to hit <img src> */
  const imageUrl =
    (typeof imageUrlRaw === 'string' && /^https?:\/\//i.test(imageUrlRaw))
      ? imageUrlRaw
      : null;

  /** -------------------------------------------------------------------------
   *  Preload the resolved URL once it exists.
   *  This makes the cache work with modern schema (Storage paths),
   *  because we only cache AFTER useStorageUrl resolves to a real https URL.
   *  ----------------------------------------------------------------------*/
  React.useEffect(() => {
    if (!imageUrl) return;
    if (!/^https?:\/\//i.test(imageUrl)) return; // must be a real URL
    if (imageCache.has(imageUrl)) return;        // already cached

    const img = new Image();
    img.src = imageUrl;
    img.onload = () => 
    //console.log(`[TemplateSection] Pre-cached ${imageUrl}`);
    imageCache.set(imageUrl, img);
  }, [imageUrl]);

  const backgroundColor = fallbackColors[idx % fallbackColors.length];


  const [imgLoaded, setImgLoaded] = useState(false);

  /* ──────────────────────────────────────────────────────────────
     Local <img> fallback handling (match HomeCard behavior)
     • Start with imageUrl (thumb if available)
     • If thumb fails, try original once
     • If original fails, stay on colour block
   ────────────────────────────────────────────────────────────── */
  const [imgSrc, setImgSrc] = useState(imageUrl || null);
  const [triedOriginal, setTriedOriginal] = useState(false);

  React.useEffect(() => {
    setImgSrc(imageUrl || null);

    // If we already preloaded this URL during the current tab session,
    // show it immediately instead of briefly flashing the placeholder.
    setImgLoaded(Boolean(imageUrl && imageCache.has(imageUrl)));

    setTriedOriginal(false);
  }, [imageUrl]);

  const handlePreviewClick = () => {
    if (isPublished) {
      console.log(`[TemplateSection] Navigating to /forms/${tpl.id}`);
      navigate(`/forms/${tpl.id}`);
    } else {
      console.log(`[TemplateSection] Form ${tpl.id} is draft; click ignored.`);
    }
  };

  return (
    <Box
      sx={{
        minWidth: TEMPLATE_CARD_WIDTH,
        width: TEMPLATE_CARD_WIDTH,
        flex: '0 0 auto',
      }}
    >
      <Card
        elevation={0}
        sx={{
          mx: 0,
          my: 1,
          width: '100%',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ButtonBase
          sx={{
            width: '100%',
            height: TEMPLATE_IMAGE_HEIGHT,
            display: 'block',
            position: 'relative',
            border: '1px solid',
            borderColor: '#ccc',
            overflow: 'hidden',
            transition: 'border-color 0.2s',
            '&:hover': {
              borderColor: isPublished ? '#1976d2' : '#ccc',
            },
          }}
          onClick={handlePreviewClick}
        >
          {/* ① coloured placeholder while loading OR when no image */}
          {(!imgSrc || !imgLoaded) && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor,
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* ② real screenshot – keep mounted so onLoad can reliably fire */}
          {imgSrc && (
            <img
              src={imgSrc}
              alt={`${tpl.name} Thumbnail`}
              loading="eager"
              decoding="async"
              onLoad={() => setImgLoaded(true)}       // 🔑 mark ready
              onError={() => {
                // Thumb failed → switch to original once (if we have it)
                if (!triedOriginal && origUrl && imgSrc !== origUrl) {
                  console.warn(
                    `[TemplateSection] thumb failed → switching to original ${origUrl}`
                  );
                  setImgSrc(origUrl);
                  setTriedOriginal(true);
                  setImgLoaded(false); // force re-load cycle for the new src
                  return;
                }

                // Otherwise stay on colour block
                setImgSrc(null);
                setImgLoaded(false);
              }}
              style={{
                position: 'absolute',
                inset: 0,
                width : '100%',
                height: '100%',
                objectFit     : tpl.forceFit === 'contain' ? 'contain' : 'cover',
                objectPosition: 'top center',
                display: 'block',
                opacity: imgLoaded ? 1 : 0,
                transition: 'opacity 120ms ease',
                zIndex: 0,
              }}
            />
          )}



          {/* grey overlay + “Draft” pill */}
          {!isPublished && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  inset   : 0,
                  backgroundColor: 'rgba(80,80,80,0.65)',
                  zIndex  : 1,
                  borderBottom : '1px solid #D3D3D3',
                  borderRadius : 'inherit',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top : 8,
                  left: 8,
                  backgroundColor: 'rgba(0,0,0,0.65)',
                  color : '#fff',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  zIndex: 2,
                }}
              >
                Draft
              </Box>
            </>
          )}
        </ButtonBase>

        {/* Transparent title only — no white CardContent section, no description */}
        <Tooltip title={tpl.name} placement="top">
          <Typography
            variant="body2"
            sx={{
              height: TEMPLATE_TITLE_HEIGHT,
              mt: 0.75,
              px: 0.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#424242',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: `${TEMPLATE_TITLE_HEIGHT}px`,
            }}
          >
            {tpl.name}
          </Typography>
        </Tooltip>
      </Card>
    </Box>
  );
});


const TemplateSection = () => {
  // STATE  ────────────────────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]); // live only
  const navigate = useNavigate();

  // FETCH LIVE TEMPLATES  ─────────────────────────────────────────────────
  useEffect(() => {
    if (LIVE_TEMPLATE_IDS.length === 0) return;

    (async () => {
      try {
        //console.log('[TemplateSection] Fetching live templates …', LIVE_TEMPLATE_IDS);

        const q = query(
          collection(db, 'forms'),
          where(documentId(), 'in', LIVE_TEMPLATE_IDS)
        );
        const snap = await getDocs(q);

        if (snap.empty) {
          console.warn('[TemplateSection] No matching templates found.');
          setTemplates([]);
          return;
        }

        const fetched = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            name: data.title || 'Untitled',
            subtitle: data.description || '',
            thumbPath: data.screenshotThumbnail || null,  // Storage path (400 px)
            origPath : data.screenshotPath      || null,  // Storage path (full)
            legacyUrl: data.screenshotUrl       || null,  // very old schema
            forceFit: data.forceFit,
            status: data.status || 'draft',
          };
        });

        const ordered = LIVE_TEMPLATE_IDS
          .map((id) => fetched.find((t) => t.id === id))
          .filter(Boolean);

        setTemplates(ordered);
        //console.log('[TemplateSection] Live templates injected ✔');
      } catch (err) {
        console.error('[TemplateSection] Error loading live templates', err);
      }
    })();
  }, []);


  // RENDER  ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        backgroundColor: '#e8eaed',
        py: TEMPLATE_SECTION_Y_PADDING,
        position: 'relative',
      }}
    >
      <Box
        component="section"
        sx={{
          px: HOME_CONTENT_X_PADDING,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            maxWidth: HOME_CONTENT_RAIL_WIDTH,
            mx: 'auto',
            width: '100%',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: TEMPLATE_SECTION_TITLE_ROW_MARGIN_BOTTOM,
              width: '100%',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              {TEMPLATE_SECTION_TITLE}
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
            }}
          >
            <Box
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
                pr: TEMPLATE_SCROLL_END_PADDING,
                pb: 0.5,
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'flex-start',
                  gap: TEMPLATE_CARD_GAP,
                  width: 'max-content',
                  minWidth: '100%',
                }}
              >
                {/* “+ New” ─────────────────────────────────────────────────────── */}
                <Box
                  sx={{
                    minWidth: TEMPLATE_CARD_WIDTH,
                    width: TEMPLATE_CARD_WIDTH,
                    flex: '0 0 auto',
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      mx: 0,
                      my: 1,
                      width: '100%',
                      boxShadow: 'none',
                      backgroundColor: 'transparent',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <CreateFormWrapperForTemplate isNewForm>
                      <ButtonBase
                        sx={{
                          width: '100%',
                          height: TEMPLATE_IMAGE_HEIGHT,
                          display: 'block',
                          position: 'relative',
                          border: '1px solid',
                          borderColor: '#ccc',
                          overflow: 'hidden',
                          transition: 'border-color 0.2s',
                          '&:hover': {
                            borderColor: '#1976d2',
                          },
                        }}
                        onClick={() => console.log('[TemplateSection] New Form clicked')}
                      >
                        <img
                          src="/startnewform.png"
                          alt="New Form Thumbnail"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'top center',
                            display: 'block',
                          }}
                        />
                      </ButtonBase>

                      {/* Transparent title only — no white CardContent section, no description */}
                      <Tooltip title="New" placement="top">
                        <Typography
                          variant="body2"
                          sx={{
                            height: TEMPLATE_TITLE_HEIGHT,
                            mt: 0.75,
                            px: 0.5,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: '#424242',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: `${TEMPLATE_TITLE_HEIGHT}px`,
                          }}
                        >
                          New
                        </Typography>
                      </Tooltip>
                    </CreateFormWrapperForTemplate>
                  </Card>
                </Box>

                {templates.map((tpl, idx) => (
                  <TemplateCard
                    key={tpl.id}
                    tpl={tpl}
                    idx={idx}
                    fallbackColors={fallbackColors}
                    navigate={navigate}
                  />
                ))}
              </Box>
            </Box>

            {/* GALLERY PILL ──────────────────────────────────────────────────── */}
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: TEMPLATE_GALLERY_PILL_WIDTH,
                backgroundColor: '#e8eaed',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ButtonBase
                onClick={() => {
                  console.log('[TemplateSection] View More clicked');
                  navigate('/template-gallery');
                }}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  boxShadow: 1,
                  transition: 'background-color 0.2s',
                  '&:hover': { backgroundColor: '#f1f1f1' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <DashboardIcon sx={{ fontSize: 16 }} />
              </ButtonBase>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  textAlign: 'center',
                }}
              >
                Gallery
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// MEMO EXPORT ────────────────────────────────────────────────────────────────
export default React.memo(TemplateSection);
